/**
 * UsersTab Component
 * Complete user management interface with CRUD operations
 * 
 * Integrates:
 * - UserTable for displaying users
 * - FilterPanel for search and filters
 * - PaginationControls for navigation
 * - UserForm for create/edit
 * - React Query hooks for data management
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, AlertCircle } from 'lucide-react';
import { getTrialRequest } from '../services/adminTrialRequestsApi';
import type { TrialRequestPrefill } from './UserForm';
import { UserTable } from './UserTable';
import { UserTableSkeleton } from './UserTableSkeleton';
import { FilterPanel } from './FilterPanel';
import { PaginationControls } from './PaginationControls';
import { UserForm } from './UserForm';
import { SubDelegatesModal } from './sub-delegates/SubDelegatesModal';
import { DeleteUserConfirm } from './DeleteUserConfirm';
import { usePagination } from '../hooks/usePagination';
import {
  useAdminUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
} from '../hooks/useAdminUsers';
import type {
  AdminUser,
  UserRole,
  CreateUserPayload,
  UpdateUserPayload,
} from '../types/admin-user.type';

type FormMode = 'create' | 'edit' | null;

/**
 * Users tab component with full CRUD functionality
 * 
 * Features:
 * - List users with pagination
 * - Search by name or email
 * - Filter by role and status
 * - Create new users
 * - Edit existing users
 * - Soft-delete users
 * - Optimistic updates
 * - Loading states
 * - Error handling
 * - Total count reflects applied filters
 */
export function UsersTab() {
  // State management
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | undefined>();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);
  const [manageDelegatesFor, setManageDelegatesFor] = useState<AdminUser | null>(null);

  // Pagination
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 50,
  });

  // Data fetching
  const { data, isLoading, error, refetch } = useAdminUsers({
    page: pagination.page,
    pageSize: pagination.pageSize,
    search: search || undefined,
    role: roleFilter || undefined,
    status: statusFilter || undefined,
  });

  // Mutations
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  // Handlers
  const handleCreateUser = () => {
    setSelectedUser(undefined);
    setTrialPrefill(undefined);
    setFormMode('create');
  };

  // Feature 100 — the team-notification email's "Review & Create Trial Account"
  // button lands on /admin/users?trialRequest=<id>: fetch the request and open
  // the create form prefilled as an FP trial. Param is consumed once so
  // closing the modal doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [trialPrefill, setTrialPrefill] = useState<TrialRequestPrefill | undefined>();
  const trialRequestId = searchParams.get('trialRequest');
  useEffect(() => {
    if (!trialRequestId) return;
    let cancelled = false;
    getTrialRequest(trialRequestId)
      .then((request) => {
        if (cancelled) return;
        setTrialPrefill({
          trialRequestId: request.id,
          email: request.businessEmail,
          name: `${request.firstName} ${request.lastName}`.trim(),
          companyName: request.companyName,
        });
        setSelectedUser(undefined);
        setFormMode('create');
      })
      .catch(() => {
        // Bad/stale link — fall through to the normal tab silently.
      })
      .finally(() => {
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('trialRequest');
              return next;
            },
            { replace: true },
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // setSearchParams is deliberately out of the deps: react-router rebuilds it
    // on every searchParams change (deps [navigate, searchParams]), so including
    // it would re-run this fetch whenever any unrelated query param moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialRequestId]);

  const handleEditUser = (userId: string) => {
    const user = data?.items.find((u) => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setFormMode('edit');
    }
  };

  const handleDeleteUser = (userId: string) => {
    const user = data?.items.find((u) => u.id === userId);
    if (user) {
      setDeleteConfirm(user);
    }
  };

  const handleManageSubDelegates = (userId: string) => {
    const user = data?.items.find((u) => u.id === userId);
    if (user) {
      setManageDelegatesFor(user);
    }
  };

  const handleFormSubmit = async (payload: CreateUserPayload | UpdateUserPayload) => {
    try {
      if (formMode === 'create') {
        await createUser.mutateAsync(payload as CreateUserPayload);
      } else if (formMode === 'edit' && selectedUser) {
        await updateUser.mutateAsync({
          id: selectedUser.id,
          payload: payload as UpdateUserPayload,
        });
      }
      
      setFormMode(null);
      setSelectedUser(undefined);
    } catch (error) {
      // Error is handled by React Query and displayed in UI
      console.error('Form submission error:', error);
      throw error; // Re-throw to keep form open
    }
  };

  const handleFormCancel = () => {
    setFormMode(null);
    setSelectedUser(undefined);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteUser.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      // Error is handled by React Query
      console.error('Delete error:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    pagination.goToFirstPage(); // Reset to page 1 on search
  };

  const handleRoleChange = (value: UserRole | '') => {
    setRoleFilter(value);
    pagination.goToFirstPage(); // Reset to page 1 on filter
  };

  const handleStatusChange = (value: 'active' | 'inactive' | '') => {
    setStatusFilter(value);
    pagination.goToFirstPage(); // Reset to page 1 on filter
  };

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Failed to load users
        </h3>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div data-testid="users-tab" className="flex flex-col h-full">
      {/* Header with action button */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Manage user profiles and permissions
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreateUser}
          className="flex items-center gap-2 px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create User
        </button>
      </div>

      {/* Filter panel */}
      <FilterPanel
        searchValue={search}
        onSearchChange={handleSearchChange}
        roleValue={roleFilter}
        onRoleChange={handleRoleChange}
        statusValue={statusFilter}
        onStatusChange={(value) => handleStatusChange(value as 'active' | 'inactive' | '')}
        searchPlaceholder="Search by name or email..."
      />

      {/* Table content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {isLoading ? (
          <UserTableSkeleton rows={pagination.pageSize} />
        ) : data ? (
          <UserTable
            users={data.items}
            totalCount={data.meta.total}
            isFiltered={!!(search || roleFilter || statusFilter)}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onManageSubDelegates={handleManageSubDelegates}
          />
        ) : null}
      </div>

      {/* Pagination controls */}
      {data && (
        <PaginationControls
          meta={data.meta}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
        />
      )}

      {/* User form modal */}
      {formMode && (
        <UserForm
          key={trialPrefill?.trialRequestId ?? 'blank'}
          user={selectedUser}
          trialPrefill={formMode === 'create' ? trialPrefill : undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isSubmitting={createUser.isPending || updateUser.isPending}
        />
      )}

      {/* Manage sub-delegates modal */}
      {manageDelegatesFor && (
        <SubDelegatesModal
          owner={manageDelegatesFor}
          onClose={() => setManageDelegatesFor(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <DeleteUserConfirm
          user={deleteConfirm}
          isPending={deleteUser.isPending}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}
